/**
 * AuthStore — Pinia store para vault unlock flow (HLOS-43).
 *
 * Coordena 4 estados:
 *   loading → setup (vault não existe) | locked (vault existe) → unlocked
 *
 * Ações:
 *   - init(): detecta se vault existe em OPFS
 *   - create(passphrase): cria vault + retorna mnemonic BIP-39 (1x visível)
 *   - unlock(passphrase): decifra vault, master key em memória
 *   - unlockWithMnemonic(words): recovery via BIP-39
 *   - dismissMnemonic(): usuário anotou as palavras; vai para locked
 *   - lock(): wipe master key da memória
 *
 * HLOS-131: Lazy load do vault
 * --------------------------
 * Type imports são type-only (erased at compile time, zero runtime impact).
 *
 * HLOS-134 (era HLOS-133.1): Dependency Injection pattern
 * -------------------------------------------------------------------------
 * Este store NÃO importa diretamente `vault/store` — em vez disso, consome
 * a interface `VaultAPI` definida em `vault/contract.ts`. A implementação
 * concreta (`VaultAdapter` em `vault/adapter.ts`) é lazy-loaded e pode
 * ser substituída em testes via `setVaultAPI()`.
 *
 * Benefícios:
 * - Acoplamento direto auth↔vault eliminado (issue A10 do plano
 *   arquitetural UI 2026-08-31 resolvida)
 * - Vault chunk lazy-loaded (HLOS-131 preservado — auth não puxa vault eagerly)
 * - Tests podem usar mockVaultAPI() sem precisar do vault real
 *
 * Constitucional:
 * - CL-07 (Modelo = ativo): vault local-first via VaultAPI
 * - CL-08 (Ética fechada): master key NUNCA persistido; secureWipe em lock
 * - CL-09 (Explicabilidade): VaultAPI documentada + tested
 * - Princípio X: sem lockout permanente
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getVaultAPI } from "../vault/adapter";
import type { VaultAPI } from "../vault/contract";
import type { AuthState, AuthError, VaultStorageBackend } from "./types";

/**
 * Storage backend configurável (default: OPFS).
 * HLOS-134: storage é injetado via VaultAPI.init() — não mais armazenado aqui.
 */
export interface AuthStoreOptions {
  backend?: VaultStorageBackend;
  /** Override para testes: storage custom (substitui OPFS/Memory default). */
  storage?: unknown; // VaultStorage — duck-typing para evitar coupling no type
}

/**
 * State store do auth.
 *
 * HLOS-134: usa VaultAPI via `getVaultAPI()` (DI pattern). Nunca importa
 * vault/store diretamente. O vault chunk continua lazy-loaded via VaultAdapter
 * (HLOS-131 preservado).
 */
export const useAuthStore = defineStore("auth", () => {
  const state = ref<AuthState>("loading");
  const error = ref<AuthError | null>(null);
  const pendingMnemonic = ref<string | null>(null);
  const backend = ref<VaultStorageBackend>("opfs");
  const lastUnlockMethod = ref<"passphrase" | "mnemonic" | null>(null);
  const isUnlockedFlag = ref(false);

  const createTimestamp = ref<number>(0);
  const DISMISS_GUARD_MS = 3000;

  /**
   * Retorna VaultAPI (lazy-inicializa VaultAdapter no primeiro uso).
   * HLOS-134: DI pattern — auth não conhece a implementação concreta.
   */
  async function api(): Promise<VaultAPI> {
    return getVaultAPI();
  }

  /**
   * Inicializa o auth: detecta se vault existe.
   * HLOS-134: delega para VaultAPI.init() (lazy load via adapter).
   */
  async function init(options: AuthStoreOptions = {}): Promise<void> {
    backend.value = options.backend ?? "opfs";
    state.value = "loading";
    error.value = null;
    isUnlockedFlag.value = false;

    try {
      const vault = await api();
      const { exists } = await vault.init({
        backend: options.backend,
        storage: options.storage,
      });
      state.value = exists ? "locked" : "setup";
    } catch (e) {
      state.value = "error";
      error.value = mapError(e, "storage_unavailable");
    }
  }

  /**
   * Cria novo vault. Retorna mnemonic para o usuário anotar (visível 1x).
   * HLOS-134: delega para VaultAPI.create().
   */
  async function create(passphrase: string): Promise<void> {
    if (!passphrase || passphrase.length < 8) {
      throw new Error("passphrase must be at least 8 characters");
    }
    state.value = "loading";
    error.value = null;
    try {
      const vault = await api();
      const { mnemonic } = await vault.create(passphrase);
      pendingMnemonic.value = mnemonic;
      createTimestamp.value = Date.now();
      state.value = "setup";
    } catch (e) {
      state.value = "error";
      error.value = mapError(e, "unknown");
      throw e;
    }
  }

  function dismissMnemonic(): void {
    console.info("[auth] dismissMnemonic called at", Date.now() - createTimestamp.value, "ms after create");
    const elapsed = Date.now() - createTimestamp.value;
    if (pendingMnemonic.value === null) return;
    if (elapsed < DISMISS_GUARD_MS) {
      console.info("[auth] dismissMnemonic IGNORED (too early,", elapsed, "ms <", DISMISS_GUARD_MS, "ms)");
      return;
    }
    pendingMnemonic.value = null;
    state.value = "locked";
    console.info("[auth] dismissMnemonic: state set to locked (accepted after guard)");
  }

  function skipUnlockAndStartOnboarding(): void {
    if (pendingMnemonic.value !== null) {
      pendingMnemonic.value = null;
    }
    lastUnlockMethod.value = "passphrase";
    state.value = "unlocked";
    isUnlockedFlag.value = true;
    console.info("[auth] skipUnlockAndStartOnboarding: state=unlocked");
  }

  /** Unlock via passphrase. HLOS-134: delega para VaultAPI.unlock(). */
  async function unlock(passphrase: string): Promise<void> {
    state.value = "loading";
    error.value = null;
    try {
      const vault = await api();
      await vault.unlock(passphrase);
      lastUnlockMethod.value = "passphrase";
      // HLOS-172 FIX (v2): popular cache ANTES de setar isUnlockedFlag=true.
      // Bug v1: isUnlockedFlag=true era setado ANTES de await refreshOnboardingCache(),
      // então o watch em `useBootOrchestrator(auth.isUnlocked)` disparava com
      // _onboardingCompletedCache ainda false. decideInitialState() lia cache stale
      // e rodava onboarding de novo.
      // Bug v2: ordem importa — refreshOnboardingCache() deve completar ANTES
      // de isUnlockedFlag virar true, para que o watch encontre cache populado.
      await refreshOnboardingCache();
      // HLOS-176: setup biométrico é explicitamente opt-in via BiometricToggle
      // na Home (Princípio V / CL-06). Sem silent setup após unlock.
      isUnlockedFlag.value = true;
      state.value = "unlocked";
      console.info("[auth] vault unlocked via passphrase");
    } catch (e) {
      state.value = "locked";
      isUnlockedFlag.value = false;
      if (e instanceof Error && e.name === "DecryptError") {
        error.value = { code: "decrypt_failed", message: "Passphrase incorreta." };
      } else {
        error.value = mapError(e, "unknown");
      }
      throw e;
    }
  }

  /** Unlock via BIP-39 mnemonic (recovery flow). HLOS-134: delega para VaultAPI. */
  async function unlockWithMnemonic(mnemonic: string): Promise<void> {
    state.value = "loading";
    error.value = null;
    try {
      const vault = await api();
      await vault.unlockWithMnemonic(mnemonic);
      lastUnlockMethod.value = "mnemonic";
      // HLOS-172 FIX (v2): mesma ordem do unlock() — refreshOnboardingCache() ANTES de isUnlockedFlag.
      await refreshOnboardingCache();
      // HLOS-176: setup biométrico é explicitamente opt-in via BiometricToggle
      // na Home (Princípio V / CL-06). Sem silent setup após unlock.
      isUnlockedFlag.value = true;
      state.value = "unlocked";
      console.info("[auth] vault unlocked via mnemonic recovery");
    } catch (e) {
      state.value = "locked";
      isUnlockedFlag.value = false;
      error.value = mapError(e, "invalid_mnemonic");
      throw e;
    }
  }

  /**
   * HLOS-140 (HLOS-57): unlock via master key raw (32 bytes) — fluxo biometric.
   *
   * O BiometricKeyManager decifra o blob OPFS via WebAuthn PRF e entrega o
   * DEK; este action delega ao VaultAPI e transita o estado do auth.
   *
   * CL-07: biometria é camada 2 — fallback passphrase permanece (unlock()).
   * CL-09: erro do vault é vago (anti side-channel), mapeado p/ decrypt_failed.
   */
  async function unlockWithMasterKey(masterKey: Uint8Array): Promise<void> {
    state.value = "loading";
    error.value = null;
    try {
      const vault = await api();
      await vault.unlockWithMasterKey(masterKey);
      // Biometric não é passphrase nem mnemonic — lastUnlockMethod fica null
      // (o biometricStore é a fonte de verdade do método biométrico).
      lastUnlockMethod.value = null;
      isUnlockedFlag.value = true;
      state.value = "unlocked";
      void refreshOnboardingCache();
      console.info("[auth] vault unlocked via master key (biometric)");
    } catch (e) {
      state.value = "locked";
      isUnlockedFlag.value = false;
      error.value = mapError(e, "decrypt_failed");
      throw e;
    }
  }

  /** Lock: wipe master key da memória. HLOS-134: delega para VaultAPI.lock(). */
  function lock(): void {
    void (async () => {
      const vault = await api();
      vault.lock();
      isUnlockedFlag.value = false;
      lastUnlockMethod.value = null;
      state.value = "locked";
      // HLOS-48: limpa master key do bridge Pyodide (dynamic import).
      void import("../pyodide/bridge").then((mod) => mod.clearMasterKey?.()).catch(() => {});
    })();
  }

  /** HLOS-134: delega para VaultAPI.readData(). */
  async function readVaultData(): Promise<Record<string, unknown> | null> {
    const vault = await api();
    if (!vault.isUnlocked()) return null;
    return vault.readData();
  }

  /** HLOS-134: delega para VaultAPI.writeData(). */
  async function writeVaultData(data: Record<string, unknown>): Promise<void> {
    const vault = await api();
    try {
      await vault.writeData(data);
    } catch (e) {
      console.error("[auth.writeVaultData] failed:", e);
      throw e;
    }
  }

  async function markOnboardingComplete(): Promise<void> {
    if (!isUnlockedFlag.value) {
      console.info("[auth] markOnboardingComplete skipped: vault locked");
      return;
    }
    try {
      const vault = await api();
      await vault.markOnboardingCompleted();
      _onboardingCompletedCache = true;
      console.info("[auth] onboardingCompleted flag persisted");
    } catch (e) {
      console.warn("[auth] markOnboardingComplete failed:", e);
    }
  }

  /**
   * HLOS-140: cache síncrono do flag `onboardingCompleted` (persistido no
   * vaultData). O `readVaultData()` é async (VaultAPI), mas consumers como
   * `useBootOrchestrator.decideInitialState()` precisam de leitura síncrona.
   * O cache é atualizado após cada unlock e no markOnboardingComplete().
   */
  let _onboardingCompletedCache = false;

  async function refreshOnboardingCache(): Promise<void> {
    try {
      const data = await readVaultData();
      _onboardingCompletedCache = data?.onboardingCompleted === true;
    } catch {
      _onboardingCompletedCache = false;
    }
  }

  function hasCompletedOnboarding(): boolean {
    return isUnlockedFlag.value && pendingMnemonic.value === null
      ? _onboardingCompletedCache
      : false;
  }

  function clearError(): void {
    error.value = null;
  }

  return {
    state,
    error,
    pendingMnemonic,
    backend,
    lastUnlockMethod,
    isUnlocked: computed(() => state.value === "unlocked"),
    init,
    create,
    dismissMnemonic,
    skipUnlockAndStartOnboarding,
    unlock,
    unlockWithMnemonic,
    unlockWithMasterKey,
    lock,
    readVaultData,
    writeVaultData,
    markOnboardingComplete,
    hasCompletedOnboarding,
    clearError,
  };
});

/** Mapeia erros para AuthError (DecryptError é vago intencionalmente). */
function mapError(e: unknown, fallback: AuthError["code"]): AuthError {
  // DecryptError é type-only import (erased at compile time); usamos
  // duck-typing via error.name === "DecryptError" para detectar erros
  // de vault sem precisar importar a classe (que puxaria o chunk eager).
  if (e instanceof Error && e.name === "DecryptError") {
    return { code: "decrypt_failed", message: "Credenciais inválidas." };
  }
  const message = e instanceof Error ? e.message : String(e);
  return { code: fallback, message };
}
